import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Pencil, GripVertical, Save,
  BookOpen, PlayCircle, FileText, HelpCircle, ClipboardList,
  ChevronDown, ChevronUp, Globe, User, Image, X,
  Code, Link as LinkIcon, FileImage, Headphones, Download, Layers
} from "lucide-react";

const LESSON_TYPES = [
  { value: "video",       label: "Video",          icon: PlayCircle,  hint: "YouTube, Vimeo, Loom, or .mp4 URL" },
  { value: "embed",       label: "Embed / iframe", icon: Code,        hint: "Any embeddable URL or raw <iframe> HTML" },
  { value: "pdf",         label: "PDF",            icon: FileText,    hint: "PDF URL — rendered inline" },
  { value: "link",        label: "External Link",  icon: LinkIcon,    hint: "Send users to an external resource" },
  { value: "html",        label: "Rich HTML",      icon: Code,        hint: "Formatted HTML content" },
  { value: "image",       label: "Image",          icon: FileImage,   hint: "Image URL with optional caption" },
  { value: "audio",       label: "Audio",          icon: Headphones,  hint: "MP3 / podcast URL" },
  { value: "download",    label: "Download",       icon: Download,    hint: "Downloadable file (slides, worksheet)" },
  { value: "text",        label: "Text / Reading", icon: FileText,    hint: "Plain text reading" },
  { value: "flashcards",  label: "Flashcards",     icon: Layers,      hint: "Front/back study cards" },
  { value: "quiz",        label: "Quiz",           icon: HelpCircle,  hint: "(Coming soon)" },
  { value: "assignment",  label: "Assignment",     icon: ClipboardList, hint: "(Coming soon)" },
];

const ACCESS_TYPES = [
  { value: "free", label: "Open" },
  { value: "premium", label: "Premium" },
  { value: "soon", label: "Coming Soon" },
];

// ============================================
// FLASHCARDS SUB-EDITOR
// ============================================
const FlashcardsEditor = ({ cards, onChange }) => {
  const update = (i, k, v) => {
    const next = [...cards];
    next[i] = { ...next[i], [k]: v };
    onChange(next);
  };
  const add = () => onChange([...cards, { front: "", back: "" }]);
  const remove = (i) => onChange(cards.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      <Label className="text-slate-600">Flashcards (front / back)</Label>
      {cards.length === 0 && <p className="text-xs text-slate-400">No cards yet.</p>}
      {cards.map((c, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-2 grid grid-cols-[1fr_1fr_auto] gap-2 items-start">
          <Input value={c.front || ""} onChange={(e) => update(i, "front", e.target.value)} placeholder="Front (question)" data-testid={`flashcard-front-${i}`} />
          <Input value={c.back || ""} onChange={(e) => update(i, "back", e.target.value)} placeholder="Back (answer)" data-testid={`flashcard-back-${i}`} />
          <Button size="icon" variant="ghost" onClick={() => remove(i)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={add} data-testid="flashcard-add">
        <Plus className="w-4 h-4 mr-1" /> Add card
      </Button>
    </div>
  );
};


// ============================================
// LESSON FORM DIALOG
// ============================================
const LessonFormDialog = ({ open, onClose, onSave, lesson, moduleId }) => {
  const [form, setForm] = useState({
    title: "", description: "", lesson_type: "video", duration_minutes: 0, pages: 0, order: 0, content: {}
  });

  useEffect(() => {
    if (lesson) {
      setForm({
        title: lesson.title || "",
        description: lesson.description || "",
        lesson_type: lesson.lesson_type || "video",
        duration_minutes: lesson.duration_minutes || 0,
        pages: lesson.pages || 0,
        order: lesson.order || 0,
        content: lesson.content || {}
      });
    } else {
      setForm({ title: "", description: "", lesson_type: "video", duration_minutes: 0, pages: 0, order: 0, content: {} });
    }
  }, [lesson, open]);

  const handleSave = () => {
    if (!form.title.trim()) { toast.error("Lesson title is required"); return; }
    onSave(form, lesson?.lesson_id);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border-slate-200 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-900">{lesson ? "Edit Lesson" : "Add Lesson"}</DialogTitle>
          <DialogDescription className="text-slate-500 sr-only">Manage lesson details</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div className="space-y-2">
            <Label className="text-slate-600">Title *</Label>
            <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              className="input-base" placeholder="e.g., Successful Interviews" data-testid="lesson-title-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-600">Description</Label>
            <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className="input-base min-h-[60px]" placeholder="Brief description..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600">Type</Label>
              <Select value={form.lesson_type} onValueChange={(v) => setForm((p) => ({ ...p, lesson_type: v }))}>
                <SelectTrigger className="input-base"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {LESSON_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">Order</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm((p) => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                className="input-base" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-slate-600">Duration (minutes)</Label>
              <Input type="number" value={form.duration_minutes} onChange={(e) => setForm((p) => ({ ...p, duration_minutes: parseInt(e.target.value) || 0 }))}
                className="input-base" placeholder="e.g., 20" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-600">Pages (for text)</Label>
              <Input type="number" value={form.pages} onChange={(e) => setForm((p) => ({ ...p, pages: parseInt(e.target.value) || 0 }))}
                className="input-base" placeholder="e.g., 10" />
            </div>
          </div>
          {/* Per-type content editor */}
          <div className="space-y-2 pt-2 border-t border-slate-100">
            <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Lesson Content</p>
            <p className="text-xs text-slate-500 -mt-1">
              {LESSON_TYPES.find((t) => t.value === form.lesson_type)?.hint || ""}
            </p>

            {form.lesson_type === "video" && (
              <div className="space-y-2">
                <Label className="text-slate-600">Video URL</Label>
                <Input value={form.content?.video_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, video_url: e.target.value } }))}
                  className="input-base" placeholder="https://www.youtube.com/watch?v=… or https://vimeo.com/… or https://…/file.mp4"
                  data-testid="content-video-url" />
                <Label className="text-slate-600">Captions / transcript URL (optional)</Label>
                <Input value={form.content?.captions_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, captions_url: e.target.value } }))}
                  className="input-base" placeholder="https://…/captions.vtt" />
              </div>
            )}

            {form.lesson_type === "embed" && (
              <div className="space-y-2">
                <Label className="text-slate-600">Embed URL (preferred)</Label>
                <Input value={form.content?.embed_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, embed_url: e.target.value } }))}
                  className="input-base" placeholder="https://docs.google.com/presentation/d/…/embed"
                  data-testid="content-embed-url" />
                <Label className="text-slate-600">Or paste raw &lt;iframe&gt; HTML</Label>
                <Textarea value={form.content?.embed_html || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, embed_html: e.target.value } }))}
                  className="input-base min-h-[80px] font-mono text-xs" placeholder='<iframe src="…" width="100%" height="500" frameborder="0" allowfullscreen></iframe>' />
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">Only paste embed HTML from sources you trust — it will be rendered as-is to learners.</p>
              </div>
            )}

            {form.lesson_type === "pdf" && (
              <div className="space-y-2">
                <Label className="text-slate-600">PDF URL</Label>
                <Input value={form.content?.pdf_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, pdf_url: e.target.value } }))}
                  className="input-base" placeholder="https://…/handbook.pdf"
                  data-testid="content-pdf-url" />
                <Label className="text-slate-600">Display label</Label>
                <Input value={form.content?.pdf_label || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, pdf_label: e.target.value } }))}
                  className="input-base" placeholder="e.g., 'CMMC Handbook v2'" />
              </div>
            )}

            {form.lesson_type === "link" && (
              <div className="space-y-2">
                <Label className="text-slate-600">URL</Label>
                <Input value={form.content?.link_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, link_url: e.target.value } }))}
                  className="input-base" placeholder="https://…"
                  data-testid="content-link-url" />
                <Label className="text-slate-600">Link title</Label>
                <Input value={form.content?.link_title || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, link_title: e.target.value } }))}
                  className="input-base" placeholder="e.g., 'NIST 800-171 spec'" />
              </div>
            )}

            {form.lesson_type === "html" && (
              <div className="space-y-2">
                <Label className="text-slate-600">HTML content</Label>
                <Textarea value={form.content?.html || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, html: e.target.value } }))}
                  className="input-base min-h-[160px] font-mono text-xs"
                  placeholder="<h2>Lesson overview</h2><p>…</p>"
                  data-testid="content-html" />
              </div>
            )}

            {form.lesson_type === "image" && (
              <div className="space-y-2">
                <Label className="text-slate-600">Image URL</Label>
                <Input value={form.content?.image_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, image_url: e.target.value } }))}
                  className="input-base" placeholder="https://…/diagram.png"
                  data-testid="content-image-url" />
                <Label className="text-slate-600">Alt text (accessibility)</Label>
                <Input value={form.content?.alt || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, alt: e.target.value } }))}
                  className="input-base" placeholder="Describe the image for screen readers" />
                <Label className="text-slate-600">Caption (optional)</Label>
                <Input value={form.content?.caption || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, caption: e.target.value } }))}
                  className="input-base" />
              </div>
            )}

            {form.lesson_type === "audio" && (
              <div className="space-y-2">
                <Label className="text-slate-600">Audio URL</Label>
                <Input value={form.content?.audio_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, audio_url: e.target.value } }))}
                  className="input-base" placeholder="https://…/episode.mp3"
                  data-testid="content-audio-url" />
                <Label className="text-slate-600">Transcript URL (optional)</Label>
                <Input value={form.content?.transcript_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, transcript_url: e.target.value } }))}
                  className="input-base" placeholder="https://…/transcript.txt" />
              </div>
            )}

            {form.lesson_type === "download" && (
              <div className="space-y-2">
                <Label className="text-slate-600">File URL</Label>
                <Input value={form.content?.file_url || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, file_url: e.target.value } }))}
                  className="input-base" placeholder="https://…/slides.pptx"
                  data-testid="content-file-url" />
                <Label className="text-slate-600">File name (shown to learner)</Label>
                <Input value={form.content?.file_name || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, file_name: e.target.value } }))}
                  className="input-base" placeholder="CMMC-Gap-Analysis.pptx" />
              </div>
            )}

            {form.lesson_type === "text" && (
              <div className="space-y-2">
                <Label className="text-slate-600">Text content</Label>
                <Textarea value={form.content?.text || ""} onChange={(e) => setForm((p) => ({ ...p, content: { ...p.content, text: e.target.value } }))}
                  className="input-base min-h-[140px]" placeholder="Lesson reading content…"
                  data-testid="content-text" />
              </div>
            )}

            {form.lesson_type === "flashcards" && (
              <FlashcardsEditor
                cards={form.content?.cards || []}
                onChange={(cards) => setForm((p) => ({ ...p, content: { ...p.content, cards } }))}
              />
            )}

            {(form.lesson_type === "quiz" || form.lesson_type === "assignment") && (
              <p className="text-xs text-slate-500 italic">Quiz / assignment authoring coming soon — for now you can describe the activity in the Description field above.</p>
            )}
          </div>

          <Button onClick={handleSave} className="w-full btn-primary" data-testid="save-lesson-btn">
            {lesson ? "Update Lesson" : "Add Lesson"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// MODULE EDITOR SECTION
// ============================================
const ModuleEditor = ({ module, courseId, onRefresh, onDelete }) => {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ title: module.title, description: module.description || "", access_type: module.access_type || "free", order: module.order || 0 });
  const [lessons, setLessons] = useState([]);
  const [lessonDialogOpen, setLessonDialogOpen] = useState(false);
  const [editLesson, setEditLesson] = useState(null);
  const [loadingLessons, setLoadingLessons] = useState(false);

  const fetchLessons = useCallback(async () => {
    setLoadingLessons(true);
    try {
      const res = await fetch(`${API}/courses/${courseId}/modules/${module.module_id}/lessons`, { credentials: "include" });
      if (res.ok) setLessons(await res.json());
    } catch { /* ignore */ }
    finally { setLoadingLessons(false); }
  }, [courseId, module.module_id]);

  useEffect(() => { fetchLessons(); }, [fetchLessons]);

  const saveModule = async () => {
    try {
      const res = await fetch(`${API}/courses/${courseId}/modules/${module.module_id}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(editForm)
      });
      if (res.ok) { toast.success("Module updated"); setEditing(false); onRefresh(); }
      else toast.error("Failed to update module");
    } catch { toast.error("Error updating module"); }
  };

  const saveLesson = async (form, lessonId) => {
    try {
      const url = lessonId
        ? `${API}/courses/${courseId}/lessons/${lessonId}`
        : `${API}/courses/${courseId}/modules/${module.module_id}/lessons`;
      const method = lessonId ? "PUT" : "POST";
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form)
      });
      if (res.ok) {
        toast.success(lessonId ? "Lesson updated" : "Lesson added");
        setLessonDialogOpen(false);
        setEditLesson(null);
        fetchLessons();
      } else toast.error("Failed to save lesson");
    } catch { toast.error("Error saving lesson"); }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm("Delete this lesson?")) return;
    try {
      const res = await fetch(`${API}/courses/${courseId}/lessons/${lessonId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Lesson deleted"); fetchLessons(); }
    } catch { toast.error("Failed to delete"); }
  };

  const getLessonIcon = (type) => {
    const map = { video: PlayCircle, text: FileText, quiz: HelpCircle, assignment: ClipboardList };
    const Icon = map[type] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`module-editor-${module.module_id}`}>
      {/* Module Header */}
      <div className="flex items-center gap-3 p-4 bg-slate-50 border-b border-slate-200">
        <div className="w-8 h-8 bg-brand-navy/10 rounded-full flex items-center justify-center flex-shrink-0">
          <span className="text-brand-navy font-bold text-xs">{String(module.order + 1).padStart(2, "0")}</span>
        </div>
        {editing ? (
          <div className="flex-1 space-y-2">
            <Input value={editForm.title} onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
              className="input-base text-sm" data-testid="edit-module-title" />
            <Input value={editForm.description} onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
              className="input-base text-sm" placeholder="Description (optional)" />
            <div className="flex gap-2">
              <Select value={editForm.access_type} onValueChange={(v) => setEditForm((p) => ({ ...p, access_type: v }))}>
                <SelectTrigger className="input-base w-32 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  {ACCESS_TYPES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input type="number" value={editForm.order} onChange={(e) => setEditForm((p) => ({ ...p, order: parseInt(e.target.value) || 0 }))}
                className="input-base w-20 text-sm" placeholder="Order" />
              <Button size="sm" className="btn-primary h-9" onClick={saveModule}><Save className="w-3.5 h-3.5 mr-1" /> Save</Button>
              <Button size="sm" variant="outline" className="btn-outline h-9" onClick={() => setEditing(false)}>Cancel</Button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-slate-900 text-sm">{module.title}</h3>
              {module.description && <p className="text-xs text-slate-500 truncate">{module.description}</p>}
            </div>
            <Badge className={`text-xs ${module.access_type === "soon" ? "bg-slate-100 text-slate-500" : module.access_type === "premium" ? "bg-brand-gold/10 text-brand-navy" : "bg-[#10B981]/10 text-[#059669]"}`}>
              {module.access_type || "free"}
            </Badge>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setEditing(true)} data-testid={`edit-module-btn-${module.module_id}`}>
              <Pencil className="w-3.5 h-3.5 text-slate-400" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={onDelete} data-testid={`delete-module-btn-${module.module_id}`}>
              <Trash2 className="w-3.5 h-3.5 text-red-400" />
            </Button>
            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </Button>
          </>
        )}
      </div>

      {/* Lessons */}
      {expanded && (
        <div className="p-4 space-y-2">
          {loadingLessons ? (
            <p className="text-xs text-slate-400 text-center py-4">Loading lessons...</p>
          ) : lessons.length > 0 ? (
            lessons.map((lesson) => (
              <div key={lesson.lesson_id} className="flex items-center justify-between p-3 bg-surface-muted rounded-lg group" data-testid={`lesson-editor-${lesson.lesson_id}`}>
                <div className="flex items-center gap-3">
                  <span className="text-slate-400">{getLessonIcon(lesson.lesson_type)}</span>
                  <div>
                    <p className="text-sm text-slate-800 font-medium">{lesson.title}</p>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <span className="capitalize">{lesson.lesson_type}</span>
                      {lesson.duration_minutes > 0 && <span>{lesson.duration_minutes} min</span>}
                      {lesson.pages > 0 && <span>{lesson.pages} pages</span>}
                    </div>
                  </div>
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                    onClick={() => { setEditLesson(lesson); setLessonDialogOpen(true); }}>
                    <Pencil className="w-3 h-3 text-slate-400" />
                  </Button>
                  <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
                    onClick={() => deleteLesson(lesson.lesson_id)}>
                    <Trash2 className="w-3 h-3 text-red-400" />
                  </Button>
                </div>
              </div>
            ))
          ) : (
            <p className="text-xs text-slate-400 text-center py-4">No lessons yet</p>
          )}
          <Button size="sm" variant="outline" className="btn-outline w-full mt-2"
            onClick={() => { setEditLesson(null); setLessonDialogOpen(true); }}
            data-testid={`add-lesson-btn-${module.module_id}`}>
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Lesson
          </Button>
        </div>
      )}

      <LessonFormDialog
        open={lessonDialogOpen}
        onClose={() => { setLessonDialogOpen(false); setEditLesson(null); }}
        onSave={saveLesson}
        lesson={editLesson}
        moduleId={module.module_id}
      />
    </div>
  );
};

// ============================================
// MAIN COURSE EDITOR PAGE
// ============================================
const CourseEditor = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isNew = !courseId;

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "", description: "", duration_weeks: 4, skill_level: "beginner",
    topics: [], max_enrollment: 30, program_id: "",
    instructor_name: "", instructor_bio: "", instructor_photo_url: "", instructor_website: ""
  });
  const [programs, setPrograms] = useState([]);
  const [newTopic, setNewTopic] = useState("");
  const [newModuleTitle, setNewModuleTitle] = useState("");

  // Load programs list for the program selector
  useEffect(() => {
    fetch(`${API}/programs`).then(r => r.ok ? r.json() : []).then(setPrograms).catch(() => {});
  }, []);

  const fetchCourse = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/courses/${courseId}`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setCourse(data);
        setForm({
          title: data.title || "",
          description: data.description || "",
          duration_weeks: data.duration_weeks || 4,
          skill_level: data.skill_level || "beginner",
          topics: data.topics || [],
          max_enrollment: data.max_enrollment || 30,
          program_id: data.program_id || "",
          instructor_name: data.instructor_name || "",
          instructor_bio: data.instructor_bio || "",
          instructor_photo_url: data.instructor_photo_url || "",
          instructor_website: data.instructor_website || "",
        });
      }
    } catch { toast.error("Failed to load material"); }
    finally { setLoading(false); }
  }, [courseId, isNew]);

  const fetchModules = useCallback(async () => {
    if (isNew) return;
    try {
      const res = await fetch(`${API}/courses/${courseId}/modules`, { credentials: "include" });
      if (res.ok) setModules(await res.json());
    } catch { /* ignore */ }
  }, [courseId, isNew]);

  useEffect(() => { fetchCourse(); fetchModules(); }, [fetchCourse, fetchModules]);

  const saveCourse = async () => {
    if (!form.title || !form.description) { toast.error("Title and description are required"); return; }
    setSaving(true);
    try {
      const url = isNew ? `${API}/courses` : `${API}/courses/${courseId}`;
      const method = isNew ? "POST" : "PUT";
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form)
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(isNew ? "Course created!" : "Course updated!");
        if (isNew) navigate(`/admin/courses/${data.course_id}/edit`, { replace: true });
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to save");
      }
    } catch { toast.error("Error saving course"); }
    finally { setSaving(false); }
  };

  const addTopic = () => {
    if (newTopic.trim()) {
      setForm((p) => ({ ...p, topics: [...p.topics, newTopic.trim()] }));
      setNewTopic("");
    }
  };

  const removeTopic = (index) => {
    setForm((p) => ({ ...p, topics: p.topics.filter((_, i) => i !== index) }));
  };

  const addModule = async () => {
    if (!newModuleTitle.trim()) { toast.error("Module title required"); return; }
    try {
      const res = await fetch(`${API}/courses/${courseId}/modules`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ title: newModuleTitle.trim(), order: modules.length, access_type: "free" })
      });
      if (res.ok) {
        toast.success("Module added");
        setNewModuleTitle("");
        fetchModules();
      }
    } catch { toast.error("Failed to add module"); }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm("Delete this module and all its lessons?")) return;
    try {
      const res = await fetch(`${API}/courses/${courseId}/modules/${moduleId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Module deleted"); fetchModules(); }
    } catch { toast.error("Failed to delete module"); }
  };

  if (loading) {
    return (
      <PageLayout title="Course Editor">
        <div className="flex items-center justify-center py-20"><div className="spinner" /></div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="space-y-8" data-testid="course-editor-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-slate-500 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">
                {isNew ? "Create New Course" : "Edit Course"}
              </h1>
              {course && <p className="text-sm text-slate-500">ID: {course.course_id}</p>}
            </div>
          </div>
          <div className="flex gap-3">
            {courseId && (
              <Link to={`/courses/${courseId}`}>
                <Button variant="outline" className="btn-outline" data-testid="preview-course-btn">
                  Preview
                </Button>
              </Link>
            )}
            <Button onClick={saveCourse} disabled={saving} className="btn-primary" data-testid="save-course-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Course"}
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info */}
            <Card className="card-surface">
              <CardHeader><CardTitle className="text-slate-900">Course Details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-600">Course Title *</Label>
                  <Input value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    className="input-base" placeholder="e.g., Confidence At Work: Career Readiness" data-testid="course-edit-title" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Description *</Label>
                  <Textarea value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    className="input-base min-h-[120px]" placeholder="Describe this learning material..." data-testid="course-edit-description" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Program</Label>
                  <Select
                    value={form.program_id || "__none__"}
                    onValueChange={(v) => setForm((p) => ({ ...p, program_id: v === "__none__" ? "" : v }))}
                  >
                    <SelectTrigger className="input-base" data-testid="course-edit-program">
                      <SelectValue placeholder="Cross-program (available to everyone)" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-200">
                      <SelectItem value="__none__">Cross-program (available to everyone)</SelectItem>
                      {programs.map((p) => (
                        <SelectItem key={p.program_id} value={p.program_id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">Which Program (career track) is this material part of? Leave as "Cross-program" if it applies to all participants.</p>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Duration (weeks)</Label>
                    <Input type="number" value={form.duration_weeks} onChange={(e) => setForm((p) => ({ ...p, duration_weeks: parseInt(e.target.value) || 0 }))}
                      className="input-base" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Skill Level</Label>
                    <Select value={form.skill_level} onValueChange={(v) => setForm((p) => ({ ...p, skill_level: v }))}>
                      <SelectTrigger className="input-base"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-white border-slate-200">
                        <SelectItem value="beginner">Beginner</SelectItem>
                        <SelectItem value="intermediate">Intermediate</SelectItem>
                        <SelectItem value="advanced">Advanced</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Max Enrollment</Label>
                    <Input type="number" value={form.max_enrollment} onChange={(e) => setForm((p) => ({ ...p, max_enrollment: parseInt(e.target.value) || 30 }))}
                      className="input-base" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Topics / What You'll Learn</Label>
                  <div className="flex gap-2">
                    <Input value={newTopic} onChange={(e) => setNewTopic(e.target.value)} className="input-base"
                      placeholder="Add a topic..."
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTopic(); } }} />
                    <Button type="button" className="btn-secondary" onClick={addTopic}><Plus className="w-4 h-4" /></Button>
                  </div>
                  {form.topics.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {form.topics.map((topic, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-3 py-1 text-xs bg-brand-navy/10 text-brand-navy rounded-full">
                          {topic}
                          <button onClick={() => removeTopic(i)} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Instructor Info */}
            <Card className="card-surface">
              <CardHeader><CardTitle className="text-slate-900 flex items-center gap-2"><User className="w-5 h-5 text-brand-navy" /> Instructor</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-600">Instructor Name</Label>
                    <Input value={form.instructor_name} onChange={(e) => setForm((p) => ({ ...p, instructor_name: e.target.value }))}
                      className="input-base" placeholder="e.g., Samantha Kelly" data-testid="instructor-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-600">Website</Label>
                    <Input value={form.instructor_website} onChange={(e) => setForm((p) => ({ ...p, instructor_website: e.target.value }))}
                      className="input-base" placeholder="https://..." data-testid="instructor-website-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Photo URL</Label>
                  <Input value={form.instructor_photo_url} onChange={(e) => setForm((p) => ({ ...p, instructor_photo_url: e.target.value }))}
                    className="input-base" placeholder="https://..." data-testid="instructor-photo-input" />
                  {form.instructor_photo_url && (
                    <img src={form.instructor_photo_url} alt="Preview" className="w-16 h-16 rounded-full object-cover mt-2" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-600">Bio</Label>
                  <Textarea value={form.instructor_bio} onChange={(e) => setForm((p) => ({ ...p, instructor_bio: e.target.value }))}
                    className="input-base min-h-[100px]" placeholder="Instructor biography..." data-testid="instructor-bio-input" />
                </div>
              </CardContent>
            </Card>

            {/* Modules & Lessons (only for existing courses) */}
            {courseId && (
              <Card className="card-surface">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-slate-900 flex items-center gap-2">
                      <BookOpen className="w-5 h-5 text-brand-navy" /> Course Sections
                    </CardTitle>
                    <span className="text-xs text-slate-400">{modules.length} module{modules.length !== 1 ? "s" : ""}</span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {modules.map((mod) => (
                    <ModuleEditor
                      key={mod.module_id}
                      module={mod}
                      courseId={courseId}
                      onRefresh={fetchModules}
                      onDelete={() => deleteModule(mod.module_id)}
                    />
                  ))}

                  {/* Add Module */}
                  <div className="flex gap-2 pt-2">
                    <Input value={newModuleTitle} onChange={(e) => setNewModuleTitle(e.target.value)}
                      className="input-base" placeholder="New section title..."
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addModule(); } }}
                      data-testid="new-module-title-input" />
                    <Button className="btn-primary" onClick={addModule} data-testid="add-module-btn">
                      <Plus className="w-4 h-4 mr-1" /> Add Section
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {isNew && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                <BookOpen className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-sm text-blue-600">Save the course first, then add sections and lessons.</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-4">
              <Card className="card-surface">
                <CardContent className="pt-6 space-y-4">
                  <h3 className="font-semibold text-slate-900 text-sm">Quick Actions</h3>
                  <Button onClick={saveCourse} disabled={saving} className="w-full btn-primary" data-testid="sidebar-save-btn">
                    <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
                  </Button>
                  {courseId && (
                    <Link to={`/courses/${courseId}`} className="block">
                      <Button variant="outline" className="w-full btn-outline">Preview Course Page</Button>
                    </Link>
                  )}
                  <Link to="/admin" className="block">
                    <Button variant="outline" className="w-full btn-outline">Back to Admin</Button>
                  </Link>
                </CardContent>
              </Card>

              {course && (
                <Card className="card-surface">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold text-slate-900 text-sm mb-3">Course Stats</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-slate-500">
                        <span>Enrolled</span>
                        <span className="text-slate-900 font-medium">{course.current_enrollment}/{course.max_enrollment}</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Sections</span>
                        <span className="text-slate-900 font-medium">{modules.length}</span>
                      </div>
                      <div className="flex justify-between text-slate-500">
                        <span>Status</span>
                        <Badge className={course.is_active ? "bg-[#10B981]/10 text-[#059669]" : "bg-red-50 text-red-500"}>
                          {course.is_active ? "Active" : "Inactive"}
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default CourseEditor;
